import React, {
  useEffect,
  useRef,
  useMemo,
  useState,
  useCallback,
  useContext,
} from 'react';
import useDebounce from '../../utils/useDebounce';
import { initEditor, destroyEditor } from './utils/init-editor.util';
import { useHistory } from 'react-router-dom';
import { EditorContext } from '../../utils/contexts';
import ImagePosition from './panels/ImagePosition';
import ImageResize from './panels/ImageResize';
import useAPI from '../../utils/api';

export const PopupContainer = ({
  hellobar,
  updateSettings,
  setIsToolbarLoaded,
  ...props
}) => {
  const debouncedSettings = useDebounce(props.settings, 200);
  const elements = useMemo(() => [], []);
  const [skipRendering, setSkipRendering] = useState(false);
  const [popup, setPopup] = useState(null);
  const [rendered, setRendered] = useState(false);
  const history = useHistory();
  const { siteSettings } = useContext(EditorContext);
  const api = useAPI();

  const onFocus = (id) => {
    if (
      [
        'conversion_cta_text',
        'thank_you_text',
        'question',
        'answer1',
        'answer2',
      ].includes(id) || id?.startsWith('form_q_')
    ) {
      return;
    }
    const path = history.location.pathname.split('/');
    path.pop();
    history.push({ pathname: [...path, 'design'].join('/') });
    hellobar.pause();
  };

  const formQuestionsRef = useRef(props.settings?.form_questions);
  useEffect(() => {
    formQuestionsRef.current = props.settings?.form_questions;
  }, [props.settings?.form_questions]);

  const handleUpdate = React.useCallback((updates) => {
    const formQEntries = Object.entries(updates).filter(([k]) => k.startsWith('form_q_'));
    if (formQEntries.length > 0) {
      const questions = [...(formQuestionsRef.current || [])];
      formQEntries.forEach(([key, value]) => {
        const idx = parseInt(key.replace('form_q_', ''), 10);
        if (!isNaN(idx) && questions[idx]) {
          questions[idx] = { ...questions[idx], label: value };
        }
      });
      updateSettings({ form_questions: questions });
      const rest = Object.fromEntries(Object.entries(updates).filter(([k]) => !k.startsWith('form_q_')));
      if (Object.keys(rest).length > 0) updateSettings(rest);
    } else {
      updateSettings(updates);
    }
  }, [updateSettings]);

  const onBlur = () => {
    hellobar.start();
  };

  useEffect(() => {
    setSkipRendering(false);
  }, [props.type, props.settings?.subtype]);

  useEffect(() => {
    const mountedHandler = async (e) => {
      await initEditor({
        customFonts: siteSettings.font_uploads,
        onUpdate: handleUpdate,
        setIsToolbarLoaded,
        id: e.data.id,
        type: props.type,
        subtype: props.settings?.subtype,
        themeId: props.settings?.theme_id,
        onFocus,
        onBlur,
        siteType: {
          shopifySite: props.settings?.shopify_site,
          bigcommerceSite: props.settings?.bigcommerce_site
        },
        plan: api.plan,
      });
      // Some popup subtypes (e.g. gamification) may not mount TinyMCE editors at all.
      // Guard all TinyMCE access so preview/publish doesn't break on show.page events.
      try {
        const editors = window.tinymce?.get?.() || [];
        editors.forEach((ed) => ed?.ui?.hide?.());
        window.tinymce?.EditorManager?.activeEditor?.ui?.show?.();
      } catch (_) {}
    };

    const unmountHandler = (e) => {
      destroyEditor(e.data?.id);
      try {
        window.tinymce?.EditorManager?.activeEditor?.ui?.show?.();
      } catch (_) {}
    };

    const removeHandler = () => {
      hellobar.removeEventListener('show', showHandler);
      hellobar.removeEventListener('show.page', showPageHandler);
      hellobar.removeEventListener('remove', removeHandler);
      hellobar.removeEventListener('editable.unmount', unmountHandler);
      hellobar.removeEventListener('editable.mounted', mountedHandler);
      destroyEditor();
      if (window.tinymce.EditorManager.activeEditor) {
        window.tinymce.EditorManager.activeEditor.ui.show();
      }
    };

    const showHandler = () => {
      hellobar.addEventListener('remove', removeHandler);
    };

    const showPageHandler = () => {
      try {
        const editors = window.tinymce?.get?.() || [];
        const first = editors[0];
        first?.ui?.setEnabled?.(true);
      } catch (_) {}
    };

    hellobar.addEventListener('editable.mounted', mountedHandler);
    hellobar.addEventListener('editable.unmount', unmountHandler);
    hellobar.addEventListener('show', showHandler);
    hellobar.addEventListener('show.page', showPageHandler);

    return removeHandler;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.type, props.settings?.subtype]);

  const renderPreview = useCallback(
    (settings) => {
      const previewElement = JSON.parse(JSON.stringify(settings));
      previewElement.template_name =
        `${settings.type}_${settings.element_subtype}`.toLowerCase();

      if (previewElement.type === 'Inline' && previewElement.width == null) {
        previewElement.width = 500;
      }

      const popup = hellobar.createPopup(previewElement);
      setPopup(popup);

      if (hellobar.popups.length > 0) {
        hellobar.remove();
      }
      return hellobar.show(popup);
    },
    [hellobar]
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const renderPopup = async () => {
      debouncedSettings.font =
        siteSettings.fonts[debouncedSettings.font_id] ||
        debouncedSettings.font_id;

      window.$RENDERED_ELEMENT = debouncedSettings;
      if (elements && debouncedSettings) {
        if (skipRendering) {
          popup.update(debouncedSettings);
        } else {
          await renderPreview(debouncedSettings);
          setRendered(true);
          setSkipRendering(true);
        }
      }
    };
    renderPopup();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    debouncedSettings,
    props.type,
    props.settings?.subtype,
    props.settings?.placement,
  ]);

  return rendered ? (
    <>
      <ImagePosition updateSettings={updateSettings} settings={props.settings} />
      <ImageResize updateSettings={updateSettings} settings={props.settings} />
    </>
  ) : null;
};
